'use client';
import useSWR from 'swr';
import { Suspense, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import './navbar.css';
import NavCategory from './navCategory';
import { cn } from '@/lib/utils';
import MenuIcon from '@/components/ui/MenuIcon';
import { useMessageStore } from '@/helpers/top_message';
import { useSizeModal } from '@/helpers/size_guide_modal';
import { MagnifyingGlass } from '@phosphor-icons/react';
import SearchUI from '@/components/search/SearchUI';
import { useAuth } from '@/helpers/user_store';
import MenuItems from './menuItem';
import { getNavbar } from '@/utils/getNavbar';
import { NavData, NavMinimalData } from '@/types/navbar/navbarTypes';
import MobileNav from './MobileNav';

export default function TopNav({
  navData,
  params,
  locale,
}: {
  navData: NavMinimalData;
  params: { countryCode: string };
  locale: string;
}) {
  const { data: fullNavData, error } = useSWR<NavData>(
    locale ? ['navbar-full', locale] : null,
    () => getNavbar(locale),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    }
  );
  const [clickedCategory, setClickedCategory] = useState('');
  const [clickedChevron, setClickedChevron] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState(0);
  const [isOpen, setIsOpen] = useState(false); //for mobile screens
  const [show, setShow] = useState(false); //for shadowsearch
  const [scrolling, setScrolling] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [isReverse, setIsReverse] = useState<boolean | null>(null);
  const [isDropDownVisible, setIsDropdownVisible] = useState(false);
  const [topbarHeight, setTopbarHeight] = useState(56);
  const [menuPosition, setMenuPosition] = useState(0); //for mobile side nav
  const { isMessageOpen } = useMessageStore();
  const { isSizeModalOpen } = useSizeModal();
  const { current_user, fetchUser, fetchOrders } = useAuth();
  const [navList, setNavList] = useState<any[]>([]);
  const [slugIndex, setSlugIndex] = useState<number>(-1);

  const categoryNames = navData?.menu_items.map(
    (category) =>
      category?.nav_menu_item_translations_id?.short_title ??
      category?.nav_menu_item_translations_id?.title
  );

  const handleScrollLock = (lock: boolean) => {
    if (lock) {
      document.body.classList.add('body-scroll-lock');
    } else {
      const scrollY = document.body.style.top;
      document.body.classList.remove('body-scroll-lock');
    }
  };

  useEffect(() => {
    if ((show || isOpen) && document !== undefined) {
      handleScrollLock(true);
    } else {
      handleScrollLock(false);
    }

    if (isOpen) {
      setShow(false);
    }

    return () => {
      handleScrollLock(false);
    };
  }, [show, isOpen]);

  //useEffect for navbar visibility on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentPosition = window.scrollY;

      if (currentPosition > scrollTop + 3) {
        setScrolling(true);
      } else if (currentPosition < scrollTop - 3) {
        setScrolling(false);
      }
      setScrollTop(currentPosition);
    };

    if (scrollTop === 0 || isOpen) {
      setScrolling(false);
    }

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollTop]);

  const navItems = navData?.menu_items?.map(
    (category) =>
      category.nav_menu_item_translations_id?.short_title ??
      category?.nav_menu_item_translations_id?.title
  );

  const navOptions = navItems && [...navItems];

  const navSlugs =
    fullNavData?.menu_items?.map(
      (category: any) =>
        category.nav_menu_item_translations_id?.sub_nav_menu[0]?.item?.slug
    ) ?? [];

  const [height, setHeight] = useState<number>(0);
  const [prevHeight, setPrevHeight] = useState<string>('');
  const contentRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  //useEffect for animating height
  useEffect(() => {
    if (contentRef?.current) {
      contentRef.current.style.height = 'max-content';

      const newHeight = contentRef.current.scrollHeight;
      const previousHeight = contentRef.current.getBoundingClientRect().height;

      contentRef.current.style.height = isDropDownVisible
        ? prevHeight + 'px'
        : '0px';

      setTimeout(() => {
        if (contentRef.current) {
          setHeight(isDropDownVisible ? newHeight : 0);
          setPrevHeight(previousHeight.toString());
        }
      }, 0);

      if (!isDropDownVisible) {
        setHeight(0);
      }

      setIsDropdownVisible(clickedCategory !== '');
    }
  }, [clickedCategory, isDropDownVisible, selectedSubcategory]);

  useEffect(() => {
    if (isClosing && contentRef.current) {
      contentRef.current.style.height = '0px';
      setClickedChevron('');
      setTimeout(() => {
        setClickedCategory('');
      }, 500);
    }
  }, [isClosing]);

  const onCloseDropdown = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
    }, 1000);
  };

  const closeSidebar = () => {
    setIsOpen(false);
    setMenuOpen(false);
    onCloseDropdown();
    setTimeout(() => {
      setMenuPosition(0);
    }, 500);
  };

  // Check for the top bar's height to translate the menu down
  useEffect(() => {
    const updateTopbarHeight = () => {
      const topbarElement = document.getElementById('topbar');
      if (topbarElement) {
        setTopbarHeight(topbarElement.clientHeight);
      } else {
        setTopbarHeight(0);
      }
    };
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.removedNodes.length || mutation.addedNodes.length) {
          updateTopbarHeight();
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    updateTopbarHeight();

    return () => observer.disconnect();
  }, [isMessageOpen, topbarHeight]);

  useEffect(() => {
    if (show === true) {
      onCloseDropdown();
      setIsOpen(false);
      setMenuOpen(false);
    }
  }, [show]);

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    const sourceData = fullNavData ?? navData;
    const mobileNav = sourceData?.menu_items
      ?.map((item: any) => {
        const { nav_menu_item_translations_id } = item;

        // Handle 'category' type
        if (nav_menu_item_translations_id?.type === 'category') {
          const firstSubNavMenu =
            nav_menu_item_translations_id?.sub_nav_menu?.[0];
          const subCategories = firstSubNavMenu?.item?.sub_categories;

          return {
            title:
              nav_menu_item_translations_id?.short_title ??
              nav_menu_item_translations_id?.title,
            subCategory:
              subCategories?.map((subCategory: any) => ({
                subcat: subCategory?.short_title ?? subCategory?.title,
                list: subCategory?.sub_sub_categories?.map((cat: any) => ({
                  label: cat?.short_title ?? cat?.title,
                  desc: cat?.description,
                  path: `/products/${firstSubNavMenu?.item?.slug}/${subCategory?.slug}/${cat?.slug}`,
                  hidden: cat?.hidden,
                })),
              })) || [],
          };
        }

        // Handle 'normal' type with multiple sub-menu items
        if (
          nav_menu_item_translations_id?.type === 'normal' &&
          !nav_menu_item_translations_id?.is_singleton
        ) {
          return {
            title:
              nav_menu_item_translations_id?.short_title ??
              nav_menu_item_translations_id?.title,
            subCategory:
              nav_menu_item_translations_id?.sub_nav_menu?.map(
                (subNav: any) => ({
                  subcat: subNav?.item?.short_title ?? subNav?.item?.title,
                  list: subNav?.item?.sub_menu_items?.map((menuItem: any) => ({
                    label:
                      menuItem?.sub_menu_item_translations_id?.short_title ??
                      menuItem?.sub_menu_item_translations_id?.title,
                    desc: menuItem?.sub_menu_item_translations_id?.description,
                    path: menuItem?.sub_menu_item_translations_id?.path,
                    hidden: false,
                  })),
                })
              ) || [],
          };
        }

        // Handle 'normal' type singleton
        if (
          nav_menu_item_translations_id?.type === 'normal' &&
          nav_menu_item_translations_id?.is_singleton
        ) {
          return {
            type: 'single',
            title:
              nav_menu_item_translations_id?.short_title ??
              nav_menu_item_translations_id?.title,
            path: nav_menu_item_translations_id?.singleton_path,
            list: [],
            hidden: false,
          };
        }

        return null;
      })
      .filter(Boolean);

    setNavList(mobileNav);
  }, [fullNavData, navData]);

  // UseEffect for chevron closing
  useEffect(() => {
    setClickedChevron(clickedCategory);
    setSelectedSubcategory(0);
  }, [clickedCategory]);

  return (
    <>
      {isDropDownVisible && (
        <div
          onClick={onCloseDropdown}
          className={cn(
            'bg-black/40 h-screen overflow-hidden fixed inset-0 z-50 hidden lg:block transition-opacity duration-200',
            isClosing ? 'opacity-0' : 'opacity-30'
          )}
        />
      )}

      <nav
        className={cn(
          'w-full items-center flex flex-col border-b-[1px] border-[#F2F2F7] sticky top-0  bg-white transition-all duration-300 transform',
          scrolling && window.scrollY > 700
            ? '-translate-y-full'
            : ' translate-y-0',
          isSizeModalOpen ? 'z-0' : 'z-50'
        )}
      >
        <Suspense fallback={null}>
          <SearchUI
            show={show}
            setShow={setShow}
            position={
              topbarHeight && scrollTop <= topbarHeight ? topbarHeight + 64 : 64
            }
          />
        </Suspense>

        <div
          className={`max-w-desktop w-full h-16 z-40 bg-white flex gap-5 md:gap-10 items-center justify-between px-4 transition-all duration-300 `}
        >
          {/* left side */}
          <div className="w-full flex gap-5 xl:gap-10 items-center justify-between">
            {/* Menu icon for mobile screens */}

            {/* Logo */}
            <Link
              onClick={() => {
                onCloseDropdown();
                setIsOpen(false);
                setMenuOpen(false);
                setShow(false);
              }}
              href={'/'}
              className="block md:w-44 w-36 aspect-[160/30]"
              aria-label="Logo"
            >
              <Image
                src={'/assets/logo.svg'}
                alt="UNIMIG Logo"
                title="UNIMIG Logo"
                width={160}
                height={30}
                priority
                className="w-full h-auto lg:hover:opacity-50 transition-opacity duration-300"
              />
            </Link>
            <ul className="hidden lg:flex items-center w-full gap-1 xl:gap-2 font-semibold text-sm h-full transition-all duration-300">
              {navOptions?.map((nav: string, index) => {
                if (!navData.menu_items[index].nav_menu_item_translations_id)
                  return null;

                return (
                  <li
                    key={index}
                    className={cn(
                      'flex gap-1 items-center cursor-pointer h-full py-2 px-3 rounded-full',
                      clickedCategory === nav ? 'bg-gray' : ''
                    )}
                    onClick={() => {
                      if (clickedCategory === nav) {
                        return onCloseDropdown();
                      }
                      if (
                        !navData.menu_items[index].nav_menu_item_translations_id
                          ?.is_singleton
                      ) {
                        setSlugIndex(index);
                        setClickedCategory((prev) => {
                          const clickedIndex = categoryNames?.indexOf(nav);

                          // Get the index of the previous clicked category
                          const prevIndex = categoryNames?.indexOf(prev);

                          // Determine the reverse value based on the indices
                          if (prevIndex < 0) {
                            setIsReverse(null);
                          } else {
                            const reverse = clickedIndex < prevIndex;
                            setIsReverse(reverse);
                          }

                          return nav;
                        });
                        setShow(false);
                      } else {
                        return onCloseDropdown();
                      }
                    }}
                  >
                    {navData.menu_items[index].nav_menu_item_translations_id
                      ?.is_singleton ? (
                      <Link
                        href={`${navData.menu_items[index].nav_menu_item_translations_id?.singleton_path}`}
                      >
                        {' '}
                        <p className="text-right whitespace-nowrap">{nav}</p>
                      </Link>
                    ) : (
                      <div className="flex items-center justify-center gap-[7px]">
                        <p className="text-right whitespace-nowrap">{nav}</p>
                        <span
                          className={`chevron ${
                            clickedChevron === nav ? 'up' : 'down'
                          }`}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            {/* Search icon for mobile screen */}
          </div>
          {/* right side */}
          <div className="flex items-center md:justify-end justify-end md:gap-2  h-10 w-full">
            <div
              onClick={() => {
                setShow(!show);
                onCloseDropdown();
              }}
              className={`w-full hidden  max-w-52 ${menuOpen ? 'opacity-0' : 'opacity-100'} transition-all duration-300   bg-off-white hover:bg-[#e8e7e7] md:flex items-center  md:py-3 rounded-[12px] cursor-pointer`}
            >
              <MagnifyingGlass size={16} className="mr-2 ml-3" />

              <p className="text-[12px] font-proxima_font font-semibold text-black/80">
                Search
              </p>
            </div>

            <div
              onClick={() => {
                setShow(!show);
                closeSidebar();
              }}
              className={`cursor-pointer flex md:hidden items-center ${menuOpen ? 'opacity-0' : 'opacity-100'} transition-all duration-300 justify-end md:justify-center h-full aspect-square rounded-full md:hover:bg-[#e8e7e7]`}
            >
              <MagnifyingGlass size={24} color="black" />
            </div>

            {/* Icons */}
            <Link
              href={'/store-locator'}
              aria-label="Store locator"
              onClick={() => {
                onCloseDropdown();
                setShow(false);
              }}
              className={`nav-store-locator cursor-pointer hidden md:flex ${menuOpen ? 'opacity-0' : 'opacity-100'} transition-all duration-300 items-center justify-end md:justify-center h-full aspect-square rounded-full hover:bg-[#e8e7e7]`}
            >
              <Image
                src={'/icons/location.svg'}
                alt="Location icon for finding store or distributor"
                title="Store Locator"
                width={20}
                height={20}
                className="h-6 w-6"
              />
            </Link>
            {navData?.auth_enabled !== false && (
              <Link
                href={current_user ? '/myAccount' : '/sign-in'}
                aria-label="Auth"
                onClick={() => {
                  onCloseDropdown();
                  setShow(false);
                }}
                className={`cursor-pointer flex ${menuOpen ? 'opacity-0' : 'opacity-100'} transition-all duration-300 items-center justify-end md:justify-center h-full aspect-square rounded-full md:hover:bg-[#e8e7e7]`}
              >
                <Image
                  src={'/icons/user.svg'}
                  alt="User icon for logging in or managing account"
                  title="Account Login"
                  width={20}
                  height={20}
                  className="h-6 w-6"
                />
              </Link>
            )}

            {/* //mobile dropdown icon */}
            <div
              className="cursor-pointer flex lg:hidden items-center  justify-end md:justify-center h-full aspect-square relative rounded-full md:hover:bg-[#e8e7e7]"
              onClick={() => {
                setMenuOpen(!menuOpen);
                setIsOpen(!isOpen);
                setClickedCategory('');
                setShow(false);
              }}
            >
              <MenuIcon isOpen={menuOpen} />
            </div>
          </div>
        </div>

        <div
          ref={contentRef}
          className={cn(
            'hidden  lg:block overflow-hidden w-full z-30 top-16 bg-white justify-center absolute transition-all duration-500 ease-fast-slow'
          )}
          style={{ height: isDropDownVisible ? height : 0 }}
        >
          {fullNavData ? (
            fullNavData.menu_items.map((category: any, index: number) => {
              if (
                category?.nav_menu_item_translations_id?.type === 'category' &&
                clickedCategory ===
                  (category?.nav_menu_item_translations_id?.short_title ||
                    category?.nav_menu_item_translations_id?.title)
              ) {
                return (
                  <div
                    className={cn(
                      'max-w-desktop mx-auto transition-opacity duration-300 flex',
                      isClosing || !isDropDownVisible
                        ? 'opacity-0'
                        : 'opacity-100'
                    )}
                    key={index}
                  >
                    <NavCategory
                      categoryData={
                        category?.nav_menu_item_translations_id?.sub_nav_menu[0]
                          ?.item?.sub_categories
                      }
                      clickedCategory={navSlugs[slugIndex]}
                      onCloseDropdown={onCloseDropdown}
                      isReverse={isReverse}
                      selectedSubcategory={selectedSubcategory}
                      setSelectedSubcategory={setSelectedSubcategory}
                    />
                  </div>
                );
              } else if (
                category?.nav_menu_item_translations_id?.type === 'normal' &&
                clickedCategory ===
                  (category?.nav_menu_item_translations_id?.short_title ||
                    category?.nav_menu_item_translations_id?.title)
              ) {
                return (
                  <div
                    key={index}
                    className={cn(
                      'max-w-desktop mx-auto transition-opacity duration-300 flex',
                      isClosing || !isDropDownVisible
                        ? 'opacity-0'
                        : 'opacity-100'
                    )}
                  >
                    <MenuItems
                      menuItem={
                        category?.nav_menu_item_translations_id?.sub_nav_menu
                      }
                      clickedCategory={clickedCategory}
                      onCloseDropdown={onCloseDropdown}
                      isReverse={isReverse}
                      selectedSubcategory={selectedSubcategory}
                      setSelectedSubcategory={setSelectedSubcategory}
                    />
                  </div>
                );
              }
              return null;
            })
          ) : clickedCategory ? (
            <div className="max-w-desktop mx-auto flex justify-center items-center py-8">
              <p className="text-sm text-[#666D80]">
                {error ? 'Unable to load menu.' : 'Loading menu...'}
              </p>
            </div>
          ) : null}
        </div>
      </nav>

      {/* //Mobile Nav */}
      <div
        style={{
          paddingTop:
            topbarHeight && scrollTop <= topbarHeight ? topbarHeight + 64 : 64,
        }}
        className={cn(
          'lg:hidden flex-col fixed top-0 z-40 bg-off-white scroll-touch overflow-auto transition-transform duration-300 ease-in-out h-full w-full',
          isOpen ? 'flex translate-y-0' : '-translate-y-full'
        )}
      >
        <div className="overflow-x-hidden flex w-full bg-off-white overflow-auto no-scrollbar scroll-touch">
          <MobileNav
            data={navList}
            closeSidebar={closeSidebar}
            authEnabled={navData?.auth_enabled !== false}
          />
        </div>
      </div>
    </>
  );
}
